/// <reference types="@angular/localize" />

import { DatePipe, LOCATION_INITIALIZED, registerLocaleData } from '@angular/common';
import { HTTP_INTERCEPTORS, HttpClient, provideHttpClient, withInterceptors, withInterceptorsFromDi } from '@angular/common/http';
import localeIt from '@angular/common/locales/it';
import { ErrorHandler, Injector, enableProdMode, importProvidersFrom, inject, provideAppInitializer, provideZoneChangeDetection } from '@angular/core';
import { MatDialogModule } from '@angular/material/dialog';
import { BrowserModule, bootstrapApplication } from '@angular/platform-browser';
import { provideAnimations } from '@angular/platform-browser/animations';
import { ExtraOptions, RouterModule } from '@angular/router';
import { NgbModule } from '@ng-bootstrap/ng-bootstrap';
import { NgIdleModule } from '@ng-idle/core';
import { TranslateLoader, TranslateModule, TranslateService } from '@ngx-translate/core';
import { NgxsReduxDevtoolsPluginModule } from '@ngxs/devtools-plugin';
import { NgxsStoragePluginModule } from '@ngxs/storage-plugin';
import { NGXS_PLUGINS, NgxsModule } from '@ngxs/store';
import {
  AutoRefreshTokenService,
  INCLUDE_BEARER_TOKEN_INTERCEPTOR_CONFIG,
  IncludeBearerTokenCondition,
  UserActivityService,
  createInterceptorCondition,
  includeBearerTokenInterceptor,
  provideKeycloak,
} from 'keycloak-angular';
import { NgcCookieConsentConfig, NgcCookieConsentModule } from 'ngx-cookieconsent';
import { QuillModule } from 'ngx-quill';
import { ToastrModule } from 'ngx-toastr';
import { AppComponent } from './app/app.component';
import { appRoutes } from './app/app.routing';
import { ConfigurationState } from './app/dsmb/store/configurations/configuration.state';
import { DecisionsState } from './app/dsmb/store/decisions/decisions.state';
import { DiscussionItemsState } from './app/dsmb/store/discussion-items/discussion-items.state';
import { DocumentCountersignState } from './app/dsmb/store/document-countersign/document-countersign.state';
import { DocumentState } from './app/dsmb/store/document/document.state';
import { MeetingPollState } from './app/dsmb/store/meeting-poll/meeting-poll.state';
import { MeetingRecordingState } from './app/dsmb/store/meeting-recording/meeting-recording.state';
import { MeetingState } from './app/dsmb/store/meeting/meeting.state';
import { TasksState } from './app/dsmb/store/tasks/tasks.state';
import { GlobalErrorHandler } from './app/platform/handlers/global-error.handler';
import { LanguageInterceptor } from './app/platform/interceptors/language.interceptor';
import { LoaderInterceptor } from './app/platform/interceptors/loader.interceptor';
import { NotAcceptableInterceptor } from './app/platform/interceptors/not-acceptable.interceptor';
import { CoreDateTimePipe } from './app/platform/pipes/core-date-time/core-date-time.pipe';
import { CoreDatePipe } from './app/platform/pipes/core-date/core-date.pipe';
import { CoreTimePipe } from './app/platform/pipes/core-time/core-time.pipe';
import { CookieService } from './app/platform/services/cookie.service';
import { NotificationsService } from './app/platform/services/notifications.service';
import { logoutPlugin } from './app/platform/store/_plugins/logout.plugin';
import { NgxsNotificationsPluginModule } from './app/platform/store/_plugins/notifications.plugin';
import { ApplicationState } from './app/platform/store/application/application.state';
import { AuthState } from './app/platform/store/auth/auth.state';
import { UserMessageState } from './app/platform/store/chat/chat.state';
import { HelpdeskState } from './app/platform/store/helpdesk/helpdesk.state';
import { LogState } from './app/platform/store/log/log.state';
import { OperationState } from './app/platform/store/operation/operation.state';
import { OPERATION_STATE_LOCAL_STORAGE_KEYS } from './app/platform/store/operation/operation.token';
import { OrganizationState } from './app/platform/store/organization/organization.state';
import { PanelState } from './app/platform/store/panel/panel.state';
import { ReportingState } from './app/platform/store/reporting/reporting.state';
import { RoleState } from './app/platform/store/role/role.state';
import { SidebarState } from './app/platform/store/sidebar/sidebar.state';
import { SIDEBAR_STATE_LOCAL_STORAGE_KEYS } from './app/platform/store/sidebar/sidebar.token';
import { StudyState } from './app/platform/store/study/study.state';
import { TimezoneState } from './app/platform/store/timezone/timezone.state';
import { TrainingHistoryState } from './app/platform/store/training-history/training-history.state';
import { UserState } from './app/platform/store/user/user.state';
import { VersionState } from './app/platform/store/version/version.state';
import { createTranslateLoader } from './app/translate-loader.factory';
import { environment } from './environments/environment';
import { TaskParticipationState } from './app/dsmb/store/task-participation/task-participation.state';
import { DocumentCardState } from './app/dsmb/store/document-card/document-card.state';
import { provideLuxonDateAdapter } from '@angular/material-luxon-adapter';
import { MAT_DATE_FORMATS } from '@angular/material/core';
import { ApplicationUserService } from './app/platform/utils/application-user.service';

export function materialFormatsFactory() {
  const appUser = inject(ApplicationUserService);
  return appUser.materialDateFormats;
}

function appInitializerFactory(translate: TranslateService, injector: Injector) {
  return () =>
    // eslint-disable-next-line @typescript-eslint/no-explicit-any
    new Promise<any>((resolve: any) => {
      const cookieService = injector.get(CookieService);
      const locationInitialized = injector.get(LOCATION_INITIALIZED, Promise.resolve(null));
      locationInitialized.then(() => {
        // this language will be used as a fallback when a translation isn't found in the current language
        translate.setDefaultLang('en');
        translate.addLangs(['en', 'it']);
        registerLocaleData(localeIt, 'it');

        const cookieLangValue = cookieService.getCookie(environment.languageCookieName);

        if (translate.getLangs().includes(cookieLangValue)) {
          translate.use(cookieLangValue).subscribe(
            () => {
              console.info(`Successfully initialized '${cookieLangValue}' language.`);
            },
            () => {
              console.error(`Problem with '${cookieLangValue}' language initialization.`);
            },
            () => {
              resolve(null);
            },
          );
        } else {
          resolve(null);
        }
      });
    });
}
/* const cookieService = injector.get(CookieService);
const cookieLangValue = cookieService.getCookie(environment.languageCookieName);
const locationInitialized = injector.get(
  LOCATION_INITIALIZED,
  Promise.resolve(null)
); */
const routerConfig: ExtraOptions = {
  useHash: true,
  onSameUrlNavigation: 'reload',
  paramsInheritanceStrategy: 'always',
};
const cookieConfig: NgcCookieConsentConfig = {
  cookie: {
    // In prod this is '.edsmb.net' so the consent is shared with the Keycloak
    // login page; elsewhere it falls back to the current hostname.
    domain: environment.cookieConsentDomain || window.location.hostname,
  },
  position: 'bottom-right',
  theme: 'classic',
  palette: {
    popup: {
      background: '#ffffffd9',
      text: '#4E5157',
      link: '#005172',
    },
    button: {
      background: '#007EA3',
      text: 'white',
      border: 'transparent',
    },
  },
  type: 'info',
  layout: 'basic',
  content: {
    href: `${window.location.href.split('#')[0]}#/privacy-policy`,
  },
};

if (environment.production) {
  enableProdMode();
}

// The Keycloak bearer token is attached only to requests aimed at our own
// Platform and DSMB APIs, never leaked to third-party hosts.
const escapeRegExp = (value: string): string => value.replace(/[.*+?^${}()|[\]\\]/g, '\\$&');
const apiUrlPattern = (host: string): RegExp => new RegExp(`^${escapeRegExp(host)}(/.*)?$`, 'i');

const platformApiBearerTokenCondition = createInterceptorCondition<IncludeBearerTokenCondition>({
  urlPattern: apiUrlPattern(environment.platformHost),
});
const dsmbApiBearerTokenCondition = createInterceptorCondition<IncludeBearerTokenCondition>({
  urlPattern: apiUrlPattern(environment.dsmbHost),
});

bootstrapApplication(AppComponent, {
  providers: [
    provideZoneChangeDetection(),
    // Keycloak OIDC bootstrap: Authorization Code + PKCE, silent SSO check
    // and inactivity-based token auto-refresh. Replaces the legacy
    // IdentityServer4 / localStorage-JWT login flow (refactored in 3.2).
    provideKeycloak({
      config: {
        url: environment.keycloakUrl,
        realm: environment.keycloakRealm,
        clientId: environment.keycloakClientId,
      },
      initOptions: {
        onLoad: 'check-sso',
        pkceMethod: 'S256',
        // The app uses hash-based routing; keep the OIDC response in the query
        // string so it does not collide with the route held in the URL fragment.
        responseMode: 'query',
        // Disable every hidden-iframe mechanism of keycloak-js (login-status
        // iframe, 3rd-party-cookie probe, silent check-sso). The SPA and
        // Keycloak are cross-site in every environment (*.ethical.ch vs
        // *.edsmb.net), so third-party cookies are always blocked: those
        // iframes can only fail. The 3p-cookie probe in particular hangs until
        // it times out and then rejects init(), which blocks the Angular
        // bootstrap entirely (blank page). We therefore:
        //  - drop silentCheckSsoRedirectUri (and keep checkLoginIframe false),
        //    which makes #check3pCookiesSupported() short-circuit instead of
        //    spawning the doomed probe iframe;
        //  - rely on a full redirect for login and on @ng-idle +
        //    withAutoRefreshToken for session lifetime.
        checkLoginIframe: false,
      },
      // No withAutoRefreshToken feature here on purpose: SessionExpiryService
      // configures AutoRefreshTokenService at runtime, deriving the activity
      // window from the refresh token (exp - iat = realm SSO Session Idle) so
      // no session duration is ever hardcoded client-side. IdleComponent
      // (@ng-idle) stays the inactivity authority for the warning + logout.
      providers: [AutoRefreshTokenService, UserActivityService],
    }),
    importProvidersFrom(
      BrowserModule,
      RouterModule.forRoot(appRoutes, routerConfig),
      NgcCookieConsentModule.forRoot(cookieConfig),
      TranslateModule.forRoot({
        loader: {
          provide: TranslateLoader,
          useFactory: createTranslateLoader,
          deps: [HttpClient],
        },
      }),
      NgIdleModule.forRoot(),
      NgxsModule.forRoot(
        [
          ApplicationState,
          AuthState,
          HelpdeskState,
          LogState,
          OperationState,
          OrganizationState,
          RoleState,
          SidebarState,
          StudyState,
          PanelState,
          ReportingState,
          TimezoneState,
          UserState,
          TasksState,
          MeetingState,
          MeetingPollState,
          ConfigurationState,
          DocumentState,
          DocumentCountersignState,
          MeetingRecordingState,
          UserMessageState,
          DiscussionItemsState,
          DecisionsState,
          VersionState,
          TrainingHistoryState,
          TaskParticipationState,
          DocumentCardState,
        ],
        {
          developmentMode: !environment.production,
        },
      ),
      NgxsStoragePluginModule.forRoot({
        keys: OPERATION_STATE_LOCAL_STORAGE_KEYS.concat(SIDEBAR_STATE_LOCAL_STORAGE_KEYS),
      }),
      NgxsReduxDevtoolsPluginModule.forRoot(),
      NgxsNotificationsPluginModule.forRoot(),

      ToastrModule.forRoot({
        timeOut: NotificationsService.TIME_OUT,
        positionClass: NotificationsService.POSITION_CLASS,
      }),
      QuillModule.forRoot({
        // Optional global configuration
        modules: {
          toolbar: [['bold', 'italic', 'underline'], ['custom-button']],
        },
      }),
      NgbModule,
      MatDialogModule,
    ),
    provideAppInitializer(() => {
      const initializerFn = appInitializerFactory(inject(TranslateService), inject(Injector));
      return initializerFn();
    }),
    {
      provide: NGXS_PLUGINS,
      useValue: logoutPlugin,
      multi: true,
    },
    {
      provide: ErrorHandler,
      useClass: GlobalErrorHandler,
    },
    {
      provide: HTTP_INTERCEPTORS,
      useClass: LoaderInterceptor,
      multi: true,
    },
    {
      provide: HTTP_INTERCEPTORS,
      useClass: LanguageInterceptor,
      multi: true,
    },
    {
      provide: HTTP_INTERCEPTORS,
      useClass: NotAcceptableInterceptor,
      multi: true,
    },
    DatePipe,
    CoreDatePipe,
    CoreDateTimePipe,
    CoreTimePipe,
    {
      provide: INCLUDE_BEARER_TOKEN_INTERCEPTOR_CONFIG,
      useValue: [platformApiBearerTokenCondition, dsmbApiBearerTokenCondition],
    },
    provideHttpClient(withInterceptorsFromDi(), withInterceptors([includeBearerTokenInterceptor])),
    provideAnimations(),
    provideLuxonDateAdapter(),
    {
      provide: MAT_DATE_FORMATS,
      useFactory: materialFormatsFactory,
    },
  ],
}).catch((err) => console.error(err));
